import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../ui/sheet';
import { siteInfo } from '../../data/mock';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Venues', path: '/venues' },
  { name: 'Activities', path: '/activities' },
  { name: 'Coaches', path: '/coaches' },
  { name: 'Pricing', path: '/pricing' },
  { name: 'Birthday', path: '/birthday' },
  { name: 'Corporate', path: '/corporate' },
  { name: 'Communities', path: '/communities' },
  { name: 'Videos', path: '/videos' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <nav
          className={`flex items-center justify-between rounded-full px-6 py-3 transition-all duration-300 ${
            isScrolled
              ? 'bg-[#004534]/95 backdrop-blur-md shadow-lg'
              : 'bg-[#004534]'
          }`}
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img loading="eager" decoding="async" fetchpriority="high" 
              src="/logo.png" 
              alt="MVP LAB Logo - Indoor Sports Club Dubai Al Quoz" 
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold text-white tracking-tight">
              MVP LAB
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'bg-[#D3FF62] text-[#004534]'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${siteInfo.phone}`}
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors"
            >
              <Phone size={16} />
              <span className="hidden xl:inline">{siteInfo.phone}</span>
            </a>
            <Link to="/book">
              <Button
                className="bg-[#D3FF62] text-[#004534] hover:bg-[#c5f050] rounded-full px-6 font-semibold"
              >
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors">
                <Menu size={24} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#004534] border-none w-full sm:w-80">
              <div className="flex flex-col h-full pt-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-2">
                    <img loading="lazy" decoding="async" 
                      src="/logo.png" 
                      alt="MVP LAB Logo - Indoor Sports Club Dubai Al Quoz" 
                      className="h-8 w-auto"
                    />
                    <span className="text-xl font-bold text-white">MVP LAB</span>
                  </div>
                </div>
                <nav className="flex flex-col gap-2">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.path}>
                      <Link
                        to={link.path}
                        className={`px-4 py-3 rounded-xl text-base font-medium transition-all ${
                          location.pathname === link.path
                            ? 'bg-[#D3FF62] text-[#004534]'
                            : 'text-white hover:bg-white/10'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-auto pb-8 space-y-4">
                  <a
                    href={`tel:${siteInfo.phone}`}
                    className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                  >
                    <Phone size={20} />
                    <span>{siteInfo.phone}</span>
                  </a>
                  <SheetClose asChild>
                    <Link to="/book" className="block">
                      <Button className="w-full bg-[#D3FF62] text-[#004534] hover:bg-[#c5f050] rounded-full font-semibold">
                        Book Now
                      </Button>
                    </Link>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Header;
